import { getEnv } from "@/utils/runtime-config";

/** Máximo que acepta el backend cuando no hay nada configurado. */
export const DEFAULT_MAX_FILE_SIZE_MB = 500;

/**
 * Tope de subida en MB, configurable con `VITE_MAX_FILE_SIZE_MB`.
 *
 * Ojo: arriba de 100 MB el backend entrega una sesión resumible en vez de una
 * signed URL. Configurar este valor por debajo de 100 deja ese camino
 * inalcanzable, porque ningún archivo llega a pesar lo suficiente.
 */
export const getMaxFileSizeMb = (): number =>
  Number(getEnv("VITE_MAX_FILE_SIZE_MB")) || DEFAULT_MAX_FILE_SIZE_MB;

export const getMaxFileSizeBytes = (): number =>
  getMaxFileSizeMb() * 1024 * 1024;

/** KB debajo de 1 MB; arriba, MB — el tope ahora es 500 MB. */
export const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(2)} MB`;

/** Extensiones por MIME, para el fallback de `isAcceptedFileType`. */
const FILE_TYPE_EXTENSIONS: Record<string, string[]> = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/jpg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

/**
 * El navegador no siempre reporta un MIME type útil (vacío, o genérico como
 * "application/octet-stream") para archivos combinados/re-guardados por
 * herramientas como iLovePDF. Cuando el MIME no coincide con lo permitido,
 * se cae a validar por extensión en vez de rechazar un archivo válido.
 */
export const isAcceptedFileType = (file: File, allowedTypes: string[]) => {
  if (allowedTypes.includes(file.type)) return true;
  const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const allowedExtensions = allowedTypes.flatMap(
    (type) => FILE_TYPE_EXTENSIONS[type] ?? [],
  );
  return allowedExtensions.includes(extension);
};
